/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from "react";
import { Product } from "../types/product";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onGenerateBarcode: (keyword?: string, barcodeItem?: string) => void;
};

const ModalGenerateBarcode: React.FC<Props> = ({
  isOpen,
  onClose,
  product,
  onGenerateBarcode,
}) => {
  const [digitCount, setDigitCount] = useState(5);
  const [components, setComponents] = useState<string[]>([]);
  const [otherComponent, setOtherComponent] = useState("");
  const [selectedComponent, setSelectedComponent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [seed, setSeed] = useState(Date.now());

  const handleAddComponent = () => {
    if (selectedComponent === "[OTHER]" && otherComponent) {
      setComponents((prev) => [...prev, otherComponent]);
      setOtherComponent("");
    } else {
      setComponents((prev) => [...prev, selectedComponent]);
    }
    setSelectedComponent("");
  };

  const generateExampleResult = (seedValue: number) => {
    const timestampNumber = seedValue;
    const dataResult = components.map((component) => {
      switch (component) {
        case "[YEAR]":
          return new Date().getFullYear().toString();
        case "[MONTH]":
          return String(new Date().getMonth() + 1).padStart(2, "0");
        case "[RANDOM_NUMBER]":
          return String(timestampNumber).slice(-digitCount);
        case "[CODE]":
          return product?.itemNumber;
        default:
          return component;
      }
    });
    return dataResult.join("");
  };

  const exampleResult = useMemo(
    () => generateExampleResult(seed),
    [components, digitCount, product?.itemNumber, seed]
  );
  const handleSave = async () => {
    setIsLoading(true);
    const newSeed = Date.now();
    const newExampleResult = generateExampleResult(newSeed);
    setSeed(newSeed);
    try {
      await onGenerateBarcode(product?.itemNumber, newExampleResult);
      onClose();
    } catch (error) {
      console.error("Error saving barcode:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white w-full max-w-2xl rounded-xl p-6 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Pengaturan Penomoran</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-800"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block font-medium">Komponen Penomoran</label>
            <div className="flex items-end gap-2 mt-1">
              <select
                className="border p-2 rounded"
                value={selectedComponent}
                onChange={(e) => {
                  setSelectedComponent(e.target.value);
                }}
              >
                <option value="" className="hidden"></option>

                <option value="[MONTH]">Bulan</option>
                <option value="[YEAR]">Tahun</option>
                <option value="[RANDOM_NUMBER]">Nomor Acak</option>
                <option value="[CODE]">Kode Barang</option>
                <option value="[OTHER]">Lainnya</option>
              </select>
              {selectedComponent === "[RANDOM_NUMBER]" && (
                <div>
                  <label className="block font-medium">
                    Jumlah Digit Counter <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    className="w-full mt-1 p-2 border rounded"
                    value={digitCount}
                    onChange={(e) => setDigitCount(Number(e.target.value))}
                  />
                </div>
              )}
              {selectedComponent === "[OTHER]" && (
                <div>
                  <input
                    type="text"
                    value={otherComponent}
                    onChange={(e) => setOtherComponent(e.target.value)}
                    className="w-full mt-1 p-2 border rounded"
                    placeholder="Masukkan komponen lainnya"
                  />
                </div>
              )}
              <button
                onClick={handleAddComponent}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                +
              </button>
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              {components.map((c, idx) => (
                <span
                  key={idx}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                >
                  {c}
                  <button
                    className="ml-2 text-red-500"
                    onClick={() =>
                      setComponents((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-medium">Contoh hasil penomoran</label>
            <p className="text-2xl font-bold mt-1">{exampleResult}</p>
            {!components.includes("[RANDOM_NUMBER]") && (
              <div className="bg-blue-500 text-white px-4 py-2 mt-2 rounded">
                • Tambahkan komponen [Nomor Acak] agar nomor berurut maju.
              </div>
            )}
            {!components.includes("[YEAR]") ||
            !components.includes("[MONTH]") ? (
              <div className="bg-blue-500 text-white px-4 py-2 mt-2 rounded">
                • Tambahkan komponen [Tahun] dan [Bulan] untuk menghindari nomor
                bentrok saat Reset Bulanan terjadi.
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Simpan {isLoading ? "..." : ""}
          </button>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded ml-2 hover:bg-gray-400"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalGenerateBarcode;